import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Button, Form, Alert, Spinner, Badge,
  Table, Dropdown, InputGroup, Modal, ListGroup, Pagination, Tab, Tabs
} from 'react-bootstrap';
import { 
  Users, Plus, Search, Filter, Phone, Mail, MapPin, 
  CreditCard, TrendingUp, Eye, Edit, UserPlus, X, Check, Ruler
} from 'lucide-react';
import customerService from '../../services/customerService';
import { useAuth } from '../../contexts/AuthContext';
import { useBoutique } from '../../contexts/BoutiqueContext';
import MeasurementsTab from '../../components/atelier/mesures/MeasurementsTab'

/**
 * Composant principal de gestion des clients
 * Interface responsive avec gestion des crédits
 * ✅ Optimisé : pagination serveur + stats serveur
 */
const CustomersList = ({ onCustomerUpdated }) => {
  // État principal
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);

  // ✅ Pagination serveur
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const pageSizeOptions = [10, 20, 30, 50];
  const [serverPagination, setServerPagination] = useState({
    totalPages: 1,
    totalElements: 0,
  });

  const { user } = useAuth();
  const { isAtelierEnabled } = useBoutique();
  const isManager = user?.role === 'MANAGER';

  // ✅ ATELIER - Onglet actif dans la modal de détail
  const [detailActiveTab, setDetailActiveTab] = useState('details');

  // Filtres et statistiques
  const [filters, setFilters] = useState({
    status: 'all', // all, vip, credit, regular, reseller
    creditStatus: 'all', // all, has_credit, no_credit
    search: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    vip: 0,
    resellers: 0,
    withCredit: 0,
    totalCredit: 0
  });

  // Modals de gestion des clients
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Responsive
  const [viewMode, setViewMode] = useState('cards'); // cards, table
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Formulaire client (création et modification)
  const [customerForm, setCustomerForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    // Champs revendeur
    isReseller: false,
    resellerDiscount: 3000
  });
  const [isEditing, setIsEditing] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Effect pour responsive
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setViewMode(mobile ? 'cards' : 'table');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ Chargement initial
  useEffect(() => {
    loadCustomers(0);
  }, []);

  // ✅ Recharger quand filtres ou taille de page changent
  useEffect(() => {
    setCurrentPage(1);
    loadCustomers(0);
  }, [filters.status, filters.creditStatus, itemsPerPage]);

  // ✅ Debounce recherche (400ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadCustomers(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /**
   * ✅ Charger les clients depuis le serveur (paginé + filtré)
   */
  const loadCustomers = async (page = 0) => {
    try {
      setLoading(true);
      setError(null);

      const serverFilters = {
        search: searchTerm.trim() || undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
        creditStatus: filters.creditStatus !== 'all' ? filters.creditStatus : undefined,
      };

      const response = await customerService.getCustomersFiltered(page, itemsPerPage, serverFilters);

      if (response.success) {
        setCustomers(response.data);
        setFilteredCustomers(response.data);

        if (response.pagination) {
          setServerPagination({
            totalPages: response.pagination.totalPages || 1,
            totalElements: response.pagination.totalElements || 0,
          });
        }

        // ✅ Stats depuis le serveur
        if (response.stats) {
          setStats({
            total: response.stats.total || 0,
            vip: response.stats.vip || 0,
            resellers: response.stats.resellers || 0,
            withCredit: response.stats.withPurchases || 0,
            totalCredit: response.stats.totalPurchases || 0,
          });
        }
      } else {
        setError(response.message || 'Erreur lors du chargement');
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des clients:', error);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= serverPagination.totalPages) {
      setCurrentPage(newPage);
      loadCustomers(newPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  /**
   * Ouvrir le modal de création
   */
  const handleOpenCreateModal = () => {
    setCustomerForm({ 
      name: '', 
      phone: '', 
      email: '', 
      address: '',
      isReseller: false,
      resellerDiscount: 3000
    });
    setIsEditing(false);
    setShowCreateModal(true);
  };

  /**
   * Ouvrir le modal de modification
   */
  const handleOpenEditModal = (customer) => {
    setCustomerForm({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      // Champs revendeur
      isReseller: customer.isReseller || false,
      resellerDiscount: customer.resellerDiscount || 3000
    });
    setSelectedCustomer(customer);
    setIsEditing(true);
    setShowCreateModal(true); // Réutiliser le même modal
  };

  /**
   * Créer ou modifier un client
   */
  const handleSaveCustomer = async () => {
    try {
      setProcessing(true);
      setError(null);

      // Validation
      const validation = customerService.validateCustomerData(customerForm);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      let response;
      if (isEditing && selectedCustomer) {
        // Modification
        response = await customerService.updateCustomer(selectedCustomer.id, customerForm);
      } else {
        // Création
        response = await customerService.createCustomer(customerForm);
      }

      if (response.success) {
        setShowCreateModal(false);
        setCustomerForm({ 
          name: '', 
          phone: '', 
          email: '', 
          address: '',
          isReseller: false,
          resellerDiscount: 3000
        });
        
        // ✅ Recharger depuis le serveur (page courante)
        await loadCustomers(currentPage - 1);
        
        // Notifier le parent si callback fourni
        if (isEditing && onCustomerUpdated) {
          onCustomerUpdated(response.data);
        }
        
      } else {
        setError(response.message || `Erreur lors de ${isEditing ? 'la modification' : 'la création'}`);
      }
    } catch (error) {
      console.error(`❌ Erreur lors de ${isEditing ? 'la modification' : 'la création'}:`, error);
      setError('Erreur de connexion');
    } finally {
      setProcessing(false);
    }
  };

  /**
   * Afficher les détails d'un client dans le modal
   */
  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setDetailActiveTab('details'); // ✅ Reset à l'onglet par défaut
    setShowDetailModal(true);
  };

  /**
   * Modifier directement depuis la vue détaillée
   */
  const handleEditFromDetail = (customer) => {
    setShowDetailModal(false); // Fermer le modal de détails
    setTimeout(() => {
      handleOpenEditModal(customer); // Ouvrir le modal de modification
    }, 300); // Délai pour l'animation
  };

  /**
   * Formater le montant des achats
   */
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading && customers.length === 0) {
    return (
      <Container fluid className="p-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Chargement des clients...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      {/* En-tête avec statistiques */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">
              <Users className="me-2" size={24} />
              Gestion des Clients
            </h2>
            <Button 
              variant="primary" 
              onClick={handleOpenCreateModal}
              size={isMobile ? "sm" : "md"}
            >
              <UserPlus size={16} className="me-1" />
              {isMobile ? 'Nouveau' : 'Nouveau Client'}
            </Button>
          </div>

          {/* Statistiques */}
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <Card className="stat-card h-100">
                <Card.Body className="text-center">
                  <Users size={24} className="text-primary mb-2" />
                  <h4 className="mb-0">{stats.total}</h4>
                  <small className="text-muted">Total Clients</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="stat-card h-100">
                <Card.Body className="text-center">
                  <TrendingUp size={24} className="text-warning mb-2" />
                  <h4 className="mb-0">{stats.vip}</h4>
                  <small className="text-muted">Clients VIP</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="stat-card h-100 border-success">
                <Card.Body className="text-center">
                  <div className="text-success mb-2">🏷️</div>
                  <h4 className="mb-0 text-success">{stats.resellers || 0}</h4>
                  <small className="text-muted">Revendeurs</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="stat-card h-100">
                <Card.Body className="text-center">
                  <CreditCard size={24} className="text-info mb-2" />
                  <h4 className="mb-0">{stats.withCredit}</h4>
                  <small className="text-muted">Avec Achats</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Barre de recherche et filtres */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Row className="g-3">
                {/* Recherche */}
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Search size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Rechercher par nom ou téléphone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <Button 
                        variant="outline-secondary"
                        onClick={() => setSearchTerm('')}
                      >
                        <X size={16} />
                      </Button>
                    )}
                  </InputGroup>
                </Col>

                {/* Filtres */}
                <Col md={3}>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                  >
                    <option value="all">Tous les clients ({stats.total})</option>
                    <option value="reseller">🏷️ Revendeurs ({stats.resellers})</option>
                    <option value="vip">⭐ VIP ({stats.vip})</option>
                    <option value="regular">Réguliers ({Math.max(0, stats.total - stats.vip - stats.resellers)})</option>
                  </Form.Select>
                </Col>

                <Col md={3}>
                  <Form.Select
                    value={filters.creditStatus}
                    onChange={(e) => setFilters({...filters, creditStatus: e.target.value})}
                  >
                    <option value="all">Tous</option>
                    <option value="has_credit">Avec achats ({stats.withCredit})</option>
                    <option value="no_credit">Sans achats ({Math.max(0, stats.total - stats.withCredit)})</option>
                  </Form.Select>
                </Col>
              </Row>

              {/* Mode d'affichage (desktop uniquement) */}
              {!isMobile && (
                <Row className="mt-3">
                  <Col>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        {filteredCustomers.length} client(s) affiché(s) sur {serverPagination.totalElements}
                      </small>
                      <div>
                        <Button
                          variant={viewMode === 'cards' ? 'primary' : 'outline-secondary'}
                          size="sm"
                          onClick={() => setViewMode('cards')}
                          className="me-1"
                        >
                          Cartes
                        </Button>
                        <Button
                          variant={viewMode === 'table' ? 'primary' : 'outline-secondary'}
                          size="sm"
                          onClick={() => setViewMode('table')}
                        >
                          Tableau
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Messages d'erreur */}
      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Liste des clients */}
      <Row>
        <Col>
          {filteredCustomers.length === 0 && !loading ? (
            <Card>
              <Card.Body className="text-center py-5">
                <Users size={48} className="text-muted mb-3" />
                <h5>Aucun client trouvé</h5>
                <p className="text-muted">
                  {searchTerm ? 
                    'Aucun client ne correspond à votre recherche.' :
                    'Commencez par créer votre premier client.'
                  }
                </p>
                <Button 
                  variant="primary" 
                  onClick={handleOpenCreateModal}
                >
                  <UserPlus size={16} className="me-1" />
                  Créer un client
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <>
              {/* Affichage en cartes (mobile et desktop) */}
              {viewMode === 'cards' && (
                <Row className="g-3">
                  {filteredCustomers.map(customer => (
                    <Col key={customer.id} xs={12} md={6} lg={4}>
                      <Card className="customer-card h-100">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="mb-0 customer-name">{customer.name}</h6>
                            <div className="d-flex gap-1 flex-wrap">
                              {customer.isReseller && (
                                <Badge bg="success" title={`Remise: ${customer.resellerDiscount} FCFA/produit`}>
                                  🏷️ Revendeur
                                </Badge>
                              )}
                              {customer.isVip && (
                                <Badge bg="warning" text="dark">VIP</Badge>
                              )}
                              {customer.totalPurchases > 0 && (
                                <Badge bg="info">Client</Badge>
                              )}
                            </div>
                          </div>

                          {/* Afficher la remise revendeur */}
                          {customer.isReseller && (
                            <div className="mb-2 p-2 bg-success bg-opacity-10 rounded small">
                              <strong>Remise revendeur:</strong> -{customer.resellerDiscount} FCFA/produit
                            </div>
                          )}

                          <div className="customer-details mb-3">
                            {customer.phone && (
                              <div className="contact-item">
                                <Phone size={14} className="me-1" />
                                <small>{customer.phone}</small>
                              </div>
                            )}
                            {customer.email && (
                              <div className="contact-item">
                                <Mail size={14} className="me-1" />
                                <small>{customer.email}</small>
                              </div>
                            )}
                            {customer.address && (
                              <div className="contact-item">
                                <MapPin size={14} className="me-1" />
                                <small>{customer.address}</small>
                              </div>
                            )}
                          </div>

                          <div className="customer-stats mb-3">
                            <Row className="g-2 text-center">
                              <Col xs={6}>
                                <div className="stat-mini">
                                  <strong>{customer.purchaseCount || 0}</strong>
                                  <br />
                                  <small className="text-muted">Achats</small>
                                </div>
                              </Col>
                              <Col xs={6}>
                                <div className="stat-mini">
                                  <strong>{formatAmount(customer.totalPurchases)}</strong>
                                  <br />
                                  <small className="text-muted">Total</small>
                                </div>
                              </Col>
                            </Row>
                          </div>

                          <div className="d-grid">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewCustomer(customer)}
                            >
                              <Eye size={14} className="me-1" />
                              Voir détails
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}

              {/* Affichage en tableau (desktop uniquement) */}
              {viewMode === 'table' && !isMobile && (
                <Card>
                  <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>Client</th>
                          <th>Contact</th>
                          <th>Statut</th>
                          <th>Achats</th>
                          <th>Total</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCustomers.map(customer => (
                          <tr key={customer.id}>
                            <td>
                              <div>
                                <strong>{customer.name}</strong>
                                {customer.address && (
                                  <div className="text-muted small">
                                    <MapPin size={12} className="me-1" />
                                    {customer.address}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div>
                                {customer.phone && (
                                  <div className="contact-item">
                                    <Phone size={12} className="me-1" />
                                    <small>{customer.phone}</small>
                                  </div>
                                )}
                                {customer.email && (
                                  <div className="contact-item">
                                    <Mail size={12} className="me-1" />
                                    <small>{customer.email}</small>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex gap-1 flex-wrap">
                                {customer.isReseller && (
                                  <Badge bg="success" title={`Remise: ${customer.resellerDiscount} FCFA`}>
                                    🏷️ Revendeur
                                  </Badge>
                                )}
                                {customer.isVip && (
                                  <Badge bg="warning" text="dark">VIP</Badge>
                                )}
                                {customer.totalPurchases > 0 ? (
                                  <Badge bg="info">Client</Badge>
                                ) : (
                                  <Badge bg="secondary">Nouveau</Badge>
                                )}
                              </div>
                            </td>
                            <td>
                              <strong>{customer.purchaseCount || 0}</strong>
                            </td>
                            <td>
                              <strong className="text-primary">
                                {formatAmount(customer.totalPurchases)}
                              </strong>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleViewCustomer(customer)}
                                  title="Voir détails"
                                >
                                  <Eye size={14} />
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => handleOpenEditModal(customer)}
                                  title="Modifier"
                                >
                                  <Edit size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Col>
      </Row>

      {/* ✅ Pagination + sélecteur taille de page */}
      {serverPagination.totalPages > 0 && (
        <Row className="mt-3 mb-4">
          <Col>
            <Card>
              <Card.Body className="py-2 px-3">
                <div className={`d-flex ${isMobile ? 'flex-column gap-2' : 'justify-content-between align-items-center'}`}>
                  {/* Sélecteur taille de page */}
                  <div className="d-flex align-items-center gap-2">
                    <small className="text-muted">Afficher</small>
                    <Form.Select
                      size="sm"
                      style={{ width: '75px' }}
                      value={itemsPerPage}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    >
                      {pageSizeOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </Form.Select>
                    <small className="text-muted">
                      sur {serverPagination.totalElements} client(s)
                    </small>
                  </div>

                  {/* Pagination */}
                  {serverPagination.totalPages > 1 && (
                    isMobile ? (
                      <div className="d-flex justify-content-center align-items-center gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={currentPage <= 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                        >
                          ‹ Préc
                        </Button>
                        <Badge bg="primary" className="px-3 py-2">
                          {currentPage}/{serverPagination.totalPages}
                        </Badge>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={currentPage >= serverPagination.totalPages}
                          onClick={() => handlePageChange(currentPage + 1)}
                        >
                          Suiv ›
                        </Button>
                      </div>
                    ) : (
                      <Pagination className="mb-0" size="sm">
                        <Pagination.First
                          disabled={currentPage <= 1}
                          onClick={() => handlePageChange(1)}
                        />
                        <Pagination.Prev
                          disabled={currentPage <= 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                        />
                        {(() => {
                          const pages = [];
                          const total = serverPagination.totalPages;
                          let start = Math.max(1, currentPage - 2);
                          let end = Math.min(total, currentPage + 2);
                          if (start > 1) {
                            pages.push(<Pagination.Item key={1} active={currentPage === 1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
                            if (start > 2) pages.push(<Pagination.Ellipsis key="start-e" disabled />);
                          }
                          for (let i = start; i <= end; i++) {
                            pages.push(
                              <Pagination.Item key={i} active={currentPage === i} onClick={() => handlePageChange(i)}>
                                {i}
                              </Pagination.Item>
                            );
                          }
                          if (end < total) {
                            if (end < total - 1) pages.push(<Pagination.Ellipsis key="end-e" disabled />);
                            pages.push(<Pagination.Item key={total} active={currentPage === total} onClick={() => handlePageChange(total)}>{total}</Pagination.Item>);
                          }
                          return pages;
                        })()}
                        <Pagination.Next
                          disabled={currentPage >= serverPagination.totalPages}
                          onClick={() => handlePageChange(currentPage + 1)}
                        />
                        <Pagination.Last
                          disabled={currentPage >= serverPagination.totalPages}
                          onClick={() => handlePageChange(serverPagination.totalPages)}
                        />
                      </Pagination>
                    )
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Modal de création/modification de client */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? (
              <>
                <Edit size={20} className="me-2" />
                Modifier le Client
              </>
            ) : (
              <>
                <UserPlus size={20} className="me-2" />
                Nouveau Client
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Nom <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={customerForm.name}
                    onChange={(e) => setCustomerForm({...customerForm, name: e.target.value})}
                    placeholder="Nom complet du client"
                    required
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Téléphone</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <Phone size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      type="tel"
                      value={customerForm.phone}
                      onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})}
                      placeholder="+221 XX XXX XX XX"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <Mail size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      type="email"
                      value={customerForm.email}
                      onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})}
                      placeholder="email@exemple.com"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Adresse</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={customerForm.address}
                    onChange={(e) => setCustomerForm({...customerForm, address: e.target.value})}
                    placeholder="Adresse complète (optionnel)"
                  />
                </Form.Group>
              </Col>
              
              {/* Section Revendeur */}
              <Col xs={12}>
                <Card className="bg-light border-0 mt-2">
                  <Card.Body className="py-3">
                    <Form.Check
                      type="switch"
                      id="isReseller"
                      label={
                        <span className="fw-bold">
                          🏷️ Client revendeur
                        </span>
                      }
                      checked={customerForm.isReseller}
                      onChange={(e) => setCustomerForm({
                        ...customerForm, 
                        isReseller: e.target.checked
                      })}
                      className="mb-2"
                    />
                    
                    {customerForm.isReseller && (
                      <Form.Group className="mt-2">
                        <Form.Label className="small text-muted">
                          Remise par produit (FCFA)
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text>-</InputGroup.Text>
                          <Form.Control
                            type="number"
                            onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                            value={customerForm.resellerDiscount}
                            onChange={(e) => setCustomerForm({
                              ...customerForm, 
                              resellerDiscount: parseInt(e.target.value) || 3000
                            })}
                            min="0"
                            step="500"
                          />
                          <InputGroup.Text>FCFA</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">
                          Cette remise sera appliquée automatiquement sur chaque produit lors des ventes.
                        </Form.Text>
                      </Form.Group>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowCreateModal(false)}
            disabled={processing}
          >
            Annuler
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveCustomer}
            disabled={processing || !customerForm.name.trim()}
          >
            {processing ? (
              <>
                <Spinner size="sm" className="me-1" />
                {isEditing ? 'Modification...' : 'Création...'}
              </>
            ) : (
              <>
                <Check size={16} className="me-1" />
                {isEditing ? 'Modifier le client' : 'Créer le client'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de détails client avec modification intégrée */}
      {/* Modal de détails client avec onglets */}
{selectedCustomer && (
  <Modal 
    show={showDetailModal} 
    onHide={() => setShowDetailModal(false)}
    size="lg"
  >
    <Modal.Header closeButton>
      <Modal.Title>
        <Users size={20} className="me-2" />
        {selectedCustomer.name}
      </Modal.Title>
    </Modal.Header>

    <Modal.Body className="p-0">
      <Tabs
        activeKey={detailActiveTab}
        onSelect={(k) => setDetailActiveTab(k)}
        className="px-3 pt-3 border-bottom-0"
        fill
      >
        {/* Onglet Détails */}
        <Tab
          eventKey="details"
          title={
            <span>
              <Users size={16} className="me-1" />
              Détails
            </span>
          }
        >
          <div className="p-4">
            <Row className="g-4">
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Informations personnelles</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="client-info">
                      <div className="info-item">
                        <strong>Nom:</strong> {selectedCustomer.name}
                      </div>
                      {selectedCustomer.phone && (
                        <div className="info-item">
                          <Phone size={14} className="me-1" />
                          <strong>Téléphone:</strong> {selectedCustomer.phone}
                        </div>
                      )}
                      {selectedCustomer.email && (
                        <div className="info-item">
                          <Mail size={14} className="me-1" />
                          <strong>Email:</strong> {selectedCustomer.email}
                        </div>
                      )}
                      {selectedCustomer.address && (
                        <div className="info-item">
                          <MapPin size={14} className="me-1" />
                          <strong>Adresse:</strong> {selectedCustomer.address}
                        </div>
                      )}
                    </div>
                    
                    {/* Section Revendeur */}
                    {selectedCustomer.isReseller && (
                      <div className="mt-3 p-3 bg-success bg-opacity-10 rounded">
                        <div className="d-flex align-items-center mb-2">
                          <Badge bg="success" className="me-2">🏷️ Revendeur</Badge>
                        </div>
                        <div className="small">
                          <strong>Remise par produit:</strong>{' '}
                          <span className="text-success fw-bold">
                            -{selectedCustomer.resellerDiscount} FCFA
                          </span>
                        </div>
                        <div className="text-muted small mt-1">
                          Cette remise est appliquée automatiquement sur chaque produit lors des ventes.
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Statistiques d'achat</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="stats-grid">
                      <div className="stat-item">
                        <div className="stat-value">{selectedCustomer.purchaseCount || 0}</div>
                        <div className="stat-label">Nombre d'achats</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value text-primary">
                          {formatAmount(selectedCustomer.totalPurchases)}
                        </div>
                        <div className="stat-label">Total des achats</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">
                          <div className="d-flex gap-1 justify-content-center flex-wrap">
                            {selectedCustomer.isReseller && (
                              <Badge bg="success">Revendeur</Badge>
                            )}
                            {selectedCustomer.isVip ? (
                              <Badge bg="warning" text="dark">VIP</Badge>
                            ) : (
                              <Badge bg="secondary">Régulier</Badge>
                            )}
                          </div>
                        </div>
                        <div className="stat-label">Statut</div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </Tab>

        {/* ✅ ATELIER - Onglet Mesures (conditionnel) */}
        {isAtelierEnabled && (
          <Tab
            eventKey="measurements"
            title={
              <span>
                <Ruler size={16} className="me-1" />
                Mesures
              </span>
            }
          >
            <div className="p-4">
              <MeasurementsTab
                customerId={selectedCustomer.id}
                customerName={selectedCustomer.name}
                isManager={isManager}
              />
            </div>
          </Tab>
        )}
      </Tabs>
    </Modal.Body>

    <Modal.Footer>
      <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
        Fermer
      </Button>
      {detailActiveTab === 'details' && (
        <Button 
          variant="primary" 
          onClick={() => handleEditFromDetail(selectedCustomer)}
        >
          <Edit size={16} className="me-1" />
          Modifier
        </Button>
      )}
    </Modal.Footer>
  </Modal>
)}

      {/* Styles CSS */}
      <style jsx>{`
        .stat-card {
          border: none;
          box-shadow: 0 2px 10px rgba(0,0,0,0.08);
          transition: transform 0.2s;
        }

        .stat-card:hover {
          transform: translateY(-2px);
        }

        .customer-card {
          border: none;
          box-shadow: 0 2px 10px rgba(0,0,0,0.06);
          transition: transform 0.2s;
        }

        .customer-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(0,0,0,0.12);
        }

        .customer-name {
          color: #2d3748;
          font-weight: 600;
        }

        .contact-item {
          display: flex;
          align-items: center;
          margin-bottom: 0.25rem;
          color: #4a5568;
        }

        .customer-details {
          min-height: 60px;
        }

        .stat-mini {
          padding: 0.5rem;
          background: #f7fafc;
          border-radius: 6px;
        }

        .client-info .info-item {
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .client-info .info-item:last-child {
          border-bottom: none;
          margin-bottom: 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }

        .stat-item {
          text-align: center;
          padding: 1rem;
          background: #f7fafc;
          border-radius: 8px;
        }

        .stat-value {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #4a5568;
        }

        @media (max-width: 768px) {
          .customer-card .customer-details {
            min-height: auto;
          }
          
          .stat-mini {
            padding: 0.25rem;
            font-size: 0.875rem;
          }
        }
      `}</style>
    </Container>
  );
};

export default CustomersList;